import React, { useState, useEffect } from "react";
import {
  Button,
  Form,
  Image,
  Input,
  Drawer,
  Select,
  Space,
  Tag,
  message,
} from "antd";
import {
  SearchOutlined,
  InfoCircleOutlined,
  DeleteOutlined,
  FormOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { reflectionAPIWithErrorHandling, reflectionHelpers } from "../../../api/reflection/reflection";
import dayjs from "dayjs";
import BrandedPageLayout from "../../../components/Dashboard/BrandedPageLayout";
import ListTableCard from "../../../components/common/ListTableCard";
import { confirmDelete } from "../../../components/common/confirmDialog";
import { actionColumnDef } from "../../../components/common/RowActions";
import ListToolbar, {
  selectVisibleColumns,
  useListToolbar,
} from "../../../components/common/ListToolbar";
import { neutral, spacing } from "../../../theme/tokens";

const DEFAULT_VISIBLE_FILTERS = [];
const DEFAULT_VISIBLE_COLUMNS = [
  "created_at", "customer", "title", "category", "status", "response",
];

export default function ReflectionTable() {
  const navigate = useNavigate();
  const [reflections, setReflections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [identityFilter, setIdentityFilter] = useState("");

  // Quick Reply States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const {
    visibleFilters, setVisibleFilters,
    visibleColumns, setVisibleColumns,
  } = useListToolbar({
    defaultVisibleFilters: DEFAULT_VISIBLE_FILTERS,
    defaultVisibleColumns: DEFAULT_VISIBLE_COLUMNS,
  });

  const fetchReflections = async (page = 1, pageSize = 10, search = "", status = "", identity = "") => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: pageSize,
        search: search.trim(),
        status: status,
        is_resident: identity === "resident" ? 1 : identity === "visitor" ? 0 : undefined,
      };
      const response = await reflectionAPIWithErrorHandling.getReflections(params);
      const { reflections: reflectionData, pagination: paginationData } = reflectionHelpers.extractReflectionList(response);

      setReflections(reflectionData);
      setPagination({
        current: paginationData.current,
        pageSize: paginationData.pageSize,
        total: paginationData.total,
      });
    } catch (error) {
      message.error(error.message || "Không thể tải danh sách phản ánh");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReflections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTableChange = (paginationConfig) => {
    fetchReflections(paginationConfig.current, paginationConfig.pageSize, searchText, statusFilter, identityFilter);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    fetchReflections(1, pagination.pageSize, value, statusFilter, identityFilter);
  };

  const handleStatusChange = (value) => {
    setStatusFilter(value);
    fetchReflections(1, pagination.pageSize, searchText, value, identityFilter);
  };

  const handleIdentityChange = (value) => {
    setIdentityFilter(value);
    fetchReflections(1, pagination.pageSize, searchText, statusFilter, value);
  };

  const handleReset = () => {
    setSearchText("");
    setStatusFilter("");
    setIdentityFilter("");
    fetchReflections(1, pagination.pageSize, "", "", "");
  };

  const handleDelete = async (record) => {
    if (await confirmDelete({ entity: "phản ánh" })) {
      try {
        await reflectionAPIWithErrorHandling.deleteReflection(record.id);
        message.success("Xóa phản ánh thành công");
        fetchReflections(pagination.current, pagination.pageSize, searchText, statusFilter);
      } catch (error) {
        message.error(error.message);
      }
    }
  };

  const getStatusTag = (status) => {
    switch (status) {
      case "received": return <Tag color="blue">Mới nhận</Tag>;
      case "processing": return <Tag color="orange">Đang xử lý</Tag>;
      case "completed": return <Tag color="green">Đã hoàn thành</Tag>;
      default: return <Tag>{status}</Tag>;
    }
  };
  const showQuickReply = (record) => {
    setSelectedRecord(record);
    form.setFieldsValue({
      response: record.response,
      status: record.status === "received" ? "processing" : record.status,
    });
    setIsModalOpen(true);
  };

  const handleQuickSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      await reflectionAPIWithErrorHandling.replyReflection(selectedRecord.id, values);
      message.success("Đã gửi phản hồi thành công!");
      setIsModalOpen(false);
      fetchReflections(pagination.current, pagination.pageSize, searchText, statusFilter);
    } catch (error) {
      if (error.name !== "ValidationError") {
        message.error(error.message || "Lỗi khi gửi phản hồi");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ===== Định nghĩa cột =====
  const columnDefs = [
    {
      key: "created_at", label: "Ngày gửi",
      column: {
        title: "Ngày gửi", dataIndex: "created_at", key: "created_at", width: 150,
        render: (date) => dayjs(date).format("DD/MM/YYYY HH:mm"),
      },
    },
    {
      key: "customer", label: "Người gửi",
      column: {
        title: "Người gửi", dataIndex: "customer", key: "customer", width: 220,
        render: (customer, record) => (
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-800">{customer?.name || "Khách vãng lai"}</span>
              {record.customer_id ? (
                <Tag color="processing" className="m-0 text-[10px] px-1 line-height-[16px]">Cư dân</Tag>
              ) : (
                <Tag color="default" className="m-0 text-[10px] px-1 line-height-[16px]">Vãng lai</Tag>
              )}
            </div>
            <div className="text-xs text-gray-500 flex items-center gap-1">
              <span className="opacity-70">SĐT:</span> {customer?.phone || record.phone || "N/A"}
            </div>
          </div>
        ),
      },
    },
    {
      key: "title", label: "Tiêu đề",
      column: {
        title: "Tiêu đề", dataIndex: "title", key: "title", ellipsis: true,
        render: (text, record) => (
          <Button
            type="link"
            onClick={() => showQuickReply(record)}
            className="text-blue-600 hover:text-blue-800 font-medium"
            style={{ padding: 0, height: "auto" }}
          >
            {text}
          </Button>
        ),
      },
    },
    {
      key: "category", label: "Danh mục",
      column: {
        title: "Danh mục", dataIndex: "category", key: "category", width: 130,
      },
    },
    {
      key: "status", label: "Trạng thái",
      column: {
        title: "Trạng thái", dataIndex: "status", key: "status", width: 130,
        render: (status) => getStatusTag(status),
      },
    },
    {
      key: "response", label: "Phản hồi",
      column: {
        title: "Phản hồi", dataIndex: "response", key: "response", width: 100, align: "center",
        render: (text) => text ? <Tag color="green">Đã có</Tag> : <Tag color="default">Chưa có</Tag>,
      },
    },
    actionColumnDef((record) => [
      { key: "info", label: "Xem chi tiết", icon: <InfoCircleOutlined />, tone: "view", onClick: () => navigate(`/reflections/${record.id}`) },
      { key: "reply", label: "Phản hồi nhanh", icon: <FormOutlined />, tone: "warning", onClick: () => showQuickReply(record) },
      { key: "delete", label: "Xóa", icon: <DeleteOutlined />, tone: "danger", onClick: () => handleDelete(record) },
    ]),
  ];

  // ===== Định nghĩa bộ lọc =====
  const filterDefs = [
    {
      key: "search", label: "Tìm kiếm", span: { xs: 24, sm: 12, md: 8 },
      field: (
        <Input.Search
          placeholder="Tìm kiếm nội dung..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onSearch={handleSearch}
          enterButton={<SearchOutlined />}
          allowClear
        />
      ),
    },
    {
      key: "status", label: "Trạng thái", span: { xs: 24, sm: 12, md: 5 },
      field: (
        <Select
          style={{ width: "100%" }}
          placeholder="Lọc theo trạng thái"
          value={statusFilter}
          onChange={handleStatusChange}
          allowClear
        >
          <Select.Option value="received">Mới nhận</Select.Option>
          <Select.Option value="processing">Đang xử lý</Select.Option>
          <Select.Option value="completed">Đã hoàn thành</Select.Option>
        </Select>
      ),
    },
    {
      key: "identity", label: "Đối tượng gửi", span: { xs: 24, sm: 12, md: 5 },
      field: (
        <Select
          style={{ width: "100%" }}
          placeholder="Đối tượng gửi"
          value={identityFilter}
          onChange={handleIdentityChange}
          allowClear
        >
          <Select.Option value="resident">Cư dân</Select.Option>
          <Select.Option value="visitor">Vãng lai</Select.Option>
        </Select>
      ),
    },
  ];

  const columns = selectVisibleColumns(columnDefs, visibleColumns);
  const hasActiveFilter = !!(searchText || statusFilter || identityFilter);

  return (
    <BrandedPageLayout
      title="Phản ánh"
      subtitle="Tiếp nhận và xử lý phản ánh của cư dân."
      breadcrumbItems={[{ title: "Phản ánh" }]}
    >
      <ListToolbar
        filterDefs={filterDefs}
        visibleFilters={visibleFilters}
        onChangeVisibleFilters={setVisibleFilters}
        columnDefs={columnDefs}
        visibleColumns={visibleColumns}
        onChangeVisibleColumns={setVisibleColumns}
        defaultVisibleColumns={DEFAULT_VISIBLE_COLUMNS}
        hasActiveFilter={hasActiveFilter}
        onClearFilter={handleReset}
        onReload={() => fetchReflections(pagination.current, pagination.pageSize, searchText, statusFilter, identityFilter)}
      />

      <ListTableCard
        columns={columns}
        dataSource={reflections}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
        }}
        onChange={handleTableChange}
        emptyText="Không có dữ liệu"
      />

      <Drawer
        title={
          <div className="flex items-center gap-2">
            <FormOutlined className="text-blue-500" />
            <span>Phản hồi nhanh: {selectedRecord?.title}</span>
          </div>
        }
        placement="right"
        width="min(560px, 100vw)"
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        destroyOnClose
        styles={{ body: { background: neutral[50], padding: spacing[6] } }}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: spacing[2] }}>
            <Button onClick={() => setIsModalOpen(false)} disabled={submitting}>
              Hủy
            </Button>
            <Button type="primary" loading={submitting} onClick={handleQuickSubmit}>
              Gửi phản hồi
            </Button>
          </div>
        }
      >
        <div className="bg-blue-50 p-3 rounded mb-4 text-sm text-blue-800">
          <div className="font-semibold mb-1">Nội dung phản ánh:</div>
          <div className="italic mb-2">{selectedRecord?.content}</div>

          {selectedRecord?.images && selectedRecord.images.length > 0 && (
            <div className="mt-3">
              <div className="font-semibold mb-2">Hình ảnh đính kèm:</div>
              <Image.PreviewGroup>
                <Space size={8} wrap>
                  {selectedRecord.images.map((img, idx) => (
                    <Image
                      key={idx}
                      width={80}
                      height={80}
                      src={img.url}
                      className="object-cover rounded border border-blue-200"
                    />
                  ))}
                </Space>
              </Image.PreviewGroup>
            </div>
          )}
        </div>

        <Form form={form} layout="vertical" className="mt-4">
          <Form.Item
            name="response"
            label="Nội dung phản hồi"
            rules={[{ required: true, message: "Vui lòng nhập nội dung phản hồi!" }]}
          >
            <Input.TextArea rows={4} placeholder="Nhập nội dung trả lời cư dân..." />
          </Form.Item>

          <Form.Item
            name="status"
            label="Cập nhật trạng thái"
            rules={[{ required: true }]}
          >
            <Select>
              <Select.Option value="processing">Đang xử lý</Select.Option>
              <Select.Option value="completed">Đã hoàn thành</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Drawer>
    </BrandedPageLayout>
  );
}
